<template>
  <StepLayout :is-focused :tracking="trackingStep" :without-left-template>
    <template #left />

    <template #right>
      <div>
        <div class="mb-12 flex items-baseline justify-between">
          <h3 :id="`heading-${step.id}`" class="heading-3">
            {{ step.title }}
          </h3>
        </div>

        <ul
          :aria-labelledby="`heading-${step.id}`"
          class="list-none"
          :class="stepOptionsClasses(step)"
          role="radiogroup"
        >
          <StepOption
            v-for="(option, index) in options"
            :key="option.trackingValue"
            :index
            :is-group-loading
            is-small
            :option
            :step
            @changed="onOptionChanged"
            @click="onOptionClick"
          />

          <li v-if="shouldDisplayToggleButton" class="mb-12">
            <LargePicker
              :aria-expanded="displayAllItems"
              class="m-auto h-full"
              :index="options.length"
              is-small
              :label="toggleLabel"
              @click="toggleDisplayAllItems"
            />
          </li>
        </ul>

        <CardGuidance
          v-if="step.guidance"
          v-bind="step.guidance"
          class="mt-16"
          :step
        />
      </div>
    </template>
  </StepLayout>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import LargePicker from '~/scopes/product/components/CustomizationFunnel/components/LargePicker/LargePicker.vue'

import type { Step } from '../../utils/types'
import CardGuidance from '../CardGuidance/CardGuidance.vue'
import StepLayout from '../StepLayout/StepLayout.vue'
import StepOption from '../StepOption/StepOption.vue'

import translations from './Step.translations'

const MAX_PICKER_ITEMS = 6

const props = withDefaults(
  defineProps<{
    step: Step
    isFocused?: boolean
    product: GetProductResponse
    withoutLeftTemplate?: boolean
  }>(),
  {
    isFocused: true,
    withoutLeftTemplate: true,
  },
)

const displayAllItems = ref(false)
const isGroupLoading = ref(false)
const i18n = useI18n()
const emit = defineEmits<{
  clicked: [Step['options'][number], Step]
}>()

const shouldDisplayToggleButton = computed(() => {
  return props.step.options.length > MAX_PICKER_ITEMS
})

const maximumAmountOfItemsToShow = computed(() => {
  return MAX_PICKER_ITEMS - 1
})

const remainingItemsCount = computed(() => {
  return props.step.options.length - maximumAmountOfItemsToShow.value
})

const options = computed(() => {
  if (props.step.id === 'grades') {
    return [...props.step.options].reverse()
  }

  if (shouldDisplayToggleButton.value && !displayAllItems.value) {
    return props.step.options.slice(0, maximumAmountOfItemsToShow.value)
  }

  return props.step.options
})

const trackingTags = computed(() => {
  const hasGoodDeal = props.step.options.some((option) => option.goodDeal)
  const hasSelectedGoodDeal = props.step.options.some(
    (option) => option.goodDeal && option.selected,
  )

  const hasStaffPick =
    props.step.id === 'grades' &&
    !props.step.options.some((option) => option.goodDeal)
  const hasSelectedStaffPick =
    hasStaffPick &&
    props.step.options.some(
      (option) => option.parameters?.grade?.value === 11 && option.selected,
    )

  return {
    // eslint-disable-next-line no-nested-ternary
    tagDisplayed: hasStaffPick
      ? 'staff_pick'
      : hasGoodDeal
        ? 'good_deal'
        : 'no_tag',
    tagSelected: hasSelectedStaffPick || hasSelectedGoodDeal,
  }
})

const trackingStep = computed(() => {
  return {
    trackingTags: trackingTags.value,
    trackingId: props.step.trackingId,
    trackingModel: props.product.model,
    trackingCategory: props.product.tracking.categoryName,
  }
})

const toggleLabel = computed(() => {
  return displayAllItems.value
    ? i18n(translations.showLess)
    : i18n(translations.showMore, {
        remaining: remainingItemsCount.value,
      })
})

function stepOptionsClasses(step: Step) {
  if (step.id === 'rakuten') {
    return 'grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12'
  }

  if (step.columns) {
    return `grid grid-cols-${step.columns} gap-x-12 gap-y-12`
  }

  return 'grid gap-y-12'
}
function toggleDisplayAllItems() {
  displayAllItems.value = !displayAllItems.value
}
function onOptionClick(option: Step['options'][number], step: Step) {
  isGroupLoading.value = true

  emit('clicked', option, step)
}
function onOptionChanged() {
  isGroupLoading.value = false
}
</script>
